import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Sparkles, Clipboard, Download, Check, Loader2, FileText, Users, MessageSquare, Search, BarChart3, Building2, TrendingUp, ChevronDown, ChevronUp, HelpCircle, StopCircle, ShieldAlert, Scale, Info, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOOLS = [
  { key: 'jd_generator', label: 'JD Generator', icon: FileText, color: 'bg-violet-600', desc: 'Generate professional job descriptions from bullet points', placeholder: 'Enter bullet points about the role:\n- Senior React Developer\n- 5+ years experience\n- Remote, India\n- Salary: 25-35 LPA\n- Skills: React, TypeScript, Node.js' },
  { key: 'resume_screening', label: 'Resume Screening', icon: Search, color: 'bg-sky-600', desc: 'Analyze resumes against job requirements', placeholder: '' },
  { key: 'interview_questions', label: 'Interview Questions', icon: MessageSquare, color: 'bg-amber-600', desc: 'Generate role-specific interview questions', placeholder: 'Enter role details:\n- Position: Senior Backend Engineer\n- Skills: Python, FastAPI, MongoDB\n- Experience Level: 5+ years\n- Focus Areas: System Design, API Architecture' },
  { key: 'candidate_insights', label: 'Candidate Insights', icon: Users, color: 'bg-emerald-600', desc: 'Get an executive summary of a candidate', placeholder: 'Paste candidate information:\n- Name, current role, experience\n- Skills and qualifications\n- Past companies and achievements' },
  { key: 'role_analyzer', label: 'Role Analyzer', icon: TrendingUp, color: 'bg-rose-600', desc: 'Market analysis, salary benchmarking & CTC assessment', placeholder: 'Enter role details:\n- Position: DevOps Engineer\n- Location: Bangalore, India\n- Experience: 3-5 years\n- Key Skills: AWS, Kubernetes, CI/CD\n- CTC Offered: 22 LPA (optional)\n- JD excerpt (optional): paste key requirements here' },
  { key: 'company_intelligence', label: 'Company Intel', icon: Building2, color: 'bg-indigo-600', desc: 'Recruiter-focused intelligence about a company', placeholder: 'Enter company name and context:\n- Company: Razorpay\n- Hiring for: Backend Engineers\n- Additional context: Client is expanding fintech team' },
];

const GUIDES = {
  jd_generator: {
    purpose: 'Transform rough bullet points into a polished, structured job description ready for publishing.',
    deliverables: 'Formatted JD with: About the Role, Key Responsibilities, Requirements, Nice to Have, and Benefits sections.',
    howToRead: 'Copy the output directly to your job posting. Sections are clearly labeled. Edit specific details (salary, company name) as needed.'
  },
  resume_screening: {
    purpose: 'Quickly assess how well a candidate\'s resume matches specific job requirements. Use Batch Compare to rank multiple candidates.',
    deliverables: 'Single mode: Match Score (0-100), Key Matching Skills, Skill Gaps, Recommendation. Batch mode: Ranked table of all candidates with scores.',
    howToRead: 'Score 80-100 = Strong Fit (proceed to interview). 50-79 = Moderate Fit (review gaps). Below 50 = Weak Fit.'
  },
  interview_questions: {
    purpose: 'Generate role-specific interview questions with evaluation criteria so interviewers know what good answers look like.',
    deliverables: '10 questions: mix of technical, behavioral, and situational. Each has evaluation tips and rating scale guidance.',
    howToRead: 'Questions are numbered with "What to look for" hints. Use the rating tips to score candidates consistently (1-5 scale for each).'
  },
  candidate_insights: {
    purpose: 'Combine CV/resume data with your personal observations to create an objective, factual candidate assessment. Upload a CV and add your notes from direct interaction.',
    deliverables: 'Unified summary combining resume facts + recruiter observations. Sections: Professional Summary, Key Skills, Experience Highlights, Interview Talking Points, Potential Concerns, Recruiter\'s Assessment.',
    howToRead: 'The summary uses only factual, verifiable information — no superlatives or subjective language. Adjust summary length (Brief/Medium/Detailed) based on whether it\'s for a quick review or a client presentation.'
  },
  role_analyzer: {
    purpose: 'Understand the full market picture for a role — salary benchmarks, CTC competitiveness, supply-demand, and hiring difficulty.',
    deliverables: 'Market Salary Range (INR), CTC Assessment (competitive/below/above market), Key Skills in Demand, Competitor Companies, Supply-Demand Assessment, Hiring Difficulty Score (1-10), Time to Fill, JD Improvement Tips.',
    howToRead: 'Difficulty 1-3 = Easy to fill. 4-6 = Moderate, expect 2-4 weeks. 7-10 = Hard to fill, consider higher CTC or broader search. CTC Assessment helps advise clients on offer competitiveness. If you include a JD, you\'ll also get actionable improvement tips.'
  },
  company_intelligence: {
    purpose: 'Get recruiter-focused intelligence about a client company to better pitch candidates and understand culture.',
    deliverables: 'Culture Summary, Recent Trends, Hiring Patterns, Candidate Pitching Tips, Red Flags.',
    howToRead: 'Use "Pitching Tips" when selling the opportunity to candidates. "Red Flags" help you prepare candidates for concerns. All intel is from publicly available information.'
  },
};

export default function RecruiterIQ() {
  const [selectedTool, setSelectedTool] = useState(TOOLS[0].key);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const abortControllerRef = useRef(null);
  const cvUploadRef = useRef(null);
  const jdUploadRef = useRef(null);
  const resumeUploadRef = useRef(null);
  const batchUploadRef = useRef(null);
  const [extracting, setExtracting] = useState(false);

  // Resume Screening specific state
  const [screenMode, setScreenMode] = useState('single'); // 'single' | 'batch'
  const [jdText, setJdText] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [batchResumes, setBatchResumes] = useState([]); // [{name, text}]
  const [uploadingJD, setUploadingJD] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingBatch, setUploadingBatch] = useState(false);

  // Candidate Insights specific state
  const [ciCandidateName, setCiCandidateName] = useState('');
  const [ciResumeText, setCiResumeText] = useState('');
  const [ciNotes, setCiNotes] = useState('');
  const [ciLength, setCiLength] = useState('medium'); // 'brief' | 'medium' | 'detailed'
  const [uploadingCiResume, setUploadingCiResume] = useState(false);
  const ciResumeUploadRef = useRef(null);

  // "Load from attached CV" picker shared by Resume Screening + Candidate Insights
  const [cvPickerOpen, setCvPickerOpen] = useState(false);
  const [cvPickerTarget, setCvPickerTarget] = useState(null); // 'screen' | 'insights'
  const [cvCandidates, setCvCandidates] = useState([]);
  const [cvPickerLoading, setCvPickerLoading] = useState(false);
  const [loadingCvText, setLoadingCvText] = useState(false);
  const [cvFilter, setCvFilter] = useState('');

  const openCvPicker = async (target) => {
    setCvPickerTarget(target);
    setCvPickerOpen(true);
    setCvFilter('');
    setCvPickerLoading(true);
    try {
      const res = await axios.get(`${API}/recruiter-iq/candidates-with-cv`, { headers: getHeaders() });
      setCvCandidates(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load candidates');
      setCvCandidates([]);
    } finally {
      setCvPickerLoading(false);
    }
  };

  const handleCvPickerSelect = async (cand) => {
    setLoadingCvText(true);
    try {
      const res = await axios.get(`${API}/recruiter-iq/cv-text/${cand.id}`, { headers: getHeaders() });
      const { candidate_name, text } = res.data;
      if (cvPickerTarget === 'screen') {
        setResumeText(text || '');
      } else if (cvPickerTarget === 'insights') {
        setCiResumeText(text || '');
        if (!ciCandidateName && candidate_name) setCiCandidateName(candidate_name);
      }
      toast.success(`Loaded CV: ${candidate_name || cand.candidate_name}`);
      setCvPickerOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load CV text');
    } finally {
      setLoadingCvText(false);
    }
  };

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const tool = TOOLS.find(t => t.key === selectedTool);
  const guide = GUIDES[selectedTool];

  const UPLOAD_TOOLS = [];

  const extractFileText = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/resume-parser/extract-text`, fd, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return res.data.text || '';
  };

  const handleJDUpload = async (file) => {
    if (!file) return;
    setUploadingJD(true);
    try {
      const text = await extractFileText(file);
      setJdText(text);
      toast.success(`JD extracted from ${file.name}`);
    } catch (e) { toast.error('Failed to extract JD text'); }
    finally { setUploadingJD(false); if (jdUploadRef.current) jdUploadRef.current.value = ''; }
  };

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setUploadingResume(true);
    try {
      const text = await extractFileText(file);
      setResumeText(text);
      toast.success(`Resume extracted from ${file.name}`);
    } catch (e) { toast.error('Failed to extract resume text'); }
    finally { setUploadingResume(false); if (resumeUploadRef.current) resumeUploadRef.current.value = ''; }
  };

  const handleBatchUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingBatch(true);
    const newResumes = [];
    for (const file of files) {
      try {
        const text = await extractFileText(file);
        newResumes.push({ name: file.name, text });
      } catch { toast.error(`Failed to extract: ${file.name}`); }
    }
    setBatchResumes(prev => [...prev, ...newResumes]);
    toast.success(`${newResumes.length} resume(s) added`);
    setUploadingBatch(false);
    if (batchUploadRef.current) batchUploadRef.current.value = '';
  };

  const handleScreenGenerate = () => {
    if (!jdText.trim()) { toast.error('Please provide the Job Description'); return; }
    let combined = '';
    if (screenMode === 'single') {
      if (!resumeText.trim()) { toast.error('Please provide the Candidate Resume'); return; }
      combined = `JOB DESCRIPTION:\n${jdText}\n\nCANDIDATE RESUME:\n${resumeText}`;
    } else {
      if (batchResumes.length === 0) { toast.error('Please upload at least one resume'); return; }
      const resumeTexts = batchResumes.map((r, i) => `--- CANDIDATE ${i+1}: ${r.name} ---\n${r.text}`).join('\n\n');
      combined = `JOB DESCRIPTION:\n${jdText}\n\nCANDIDATES TO COMPARE AND RANK:\n${resumeTexts}`;
    }
    setInput(combined);
    generate(combined);
  };

  const handleCiResumeUpload = async (file) => {
    if (!file) return;
    setUploadingCiResume(true);
    try {
      const text = await extractFileText(file);
      setCiResumeText(text);
      toast.success(`CV extracted from ${file.name}`);
    } catch (e) { toast.error('Failed to extract CV text'); }
    finally { setUploadingCiResume(false); if (ciResumeUploadRef.current) ciResumeUploadRef.current.value = ''; }
  };

  const handleCandidateInsightsGenerate = () => {
    const hasCv = !!ciResumeText.trim();
    const hasNotes = !!ciNotes.trim();
    if (!hasCv && !hasNotes) {
      toast.error('Add a CV, recruiter observations, or both');
      return;
    }
    const lengthDirective = ciLength === 'brief' ? 'Target ~150-250 words.'
      : ciLength === 'detailed' ? 'Target ~700-1000 words.'
      : 'Target ~400-600 words.';
    const parts = [];
    if (ciCandidateName.trim()) parts.push(`Candidate: ${ciCandidateName.trim()}`);
    parts.push(`Desired length: ${ciLength}. ${lengthDirective}`);
    parts.push('=== CV / RESUME ===');
    parts.push(hasCv ? ciResumeText.trim() : '(No CV supplied)');
    parts.push('=== RECRUITER OBSERVATIONS ===');
    parts.push(hasNotes ? ciNotes.trim() : '(No recruiter notes supplied)');
    const combined = parts.join('\n\n');
    setInput(combined);
    generate(combined);
  };

  const handleCVUpload = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/resume-parser/extract-text`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const text = res.data.text || '';
      setInput(prev => prev ? `${prev}\n\n--- Resume: ${file.name} ---\n${text}` : text);
      toast.success(`Extracted text from ${file.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to extract text from file'); }
    finally { setExtracting(false); if (cvUploadRef.current) cvUploadRef.current.value = ''; }
  };

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStreaming(false);
    setLoading(false);
  };

  const generate = async (inputOverride = null) => {
    // Defensive: if invoked directly as an onClick handler (legacy callers), the
    // React MouseEvent gets passed as inputOverride and lacks .trim(). Treat any
    // non-string override as "no override" so the current `input` state is used.
    const safeOverride = (typeof inputOverride === 'string') ? inputOverride : null;
    const effectiveInput = safeOverride !== null ? safeOverride : input;
    if (!effectiveInput.trim()) { toast.error('Please enter your requirements'); return; }
    setLoading(true); setStreaming(true); setResult('');
    abortControllerRef.current = new AbortController();

    // ---- Non-streaming fallback (used when streaming fails or proxy buffers) ----
    const runNonStreaming = async () => {
      try {
        setStreaming(false);
        const res = await axios.post(
          `${API}/recruiter-iq/generate`,
          { tool: selectedTool, input: effectiveInput },
          { headers: getHeaders(), timeout: 120000 }
        );
        setResult(res.data.result || 'No result generated.');
      } catch (fallbackErr) {
        toast.error(fallbackErr.response?.data?.detail || 'Generation failed. Please try again.');
      }
    };

    // Production reverse proxies sometimes buffer SSE. If no chunk arrives within
    // 12s, abort the stream and fall back to the non-streaming endpoint so the
    // user never sees a "hanging spinner".
    let firstChunkArrived = false;
    const firstChunkTimer = setTimeout(() => {
      if (!firstChunkArrived) {
        try { abortControllerRef.current?.abort(); } catch (err) { console.error("./pages/RecruiterIQ.jsx:", err); }
        runNonStreaming();
      }
    }, 12000);

    try {
      const response = await fetch(`${API}/recruiter-iq/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tool: selectedTool, input: effectiveInput }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        clearTimeout(firstChunkTimer);
        const err = await response.json().catch(() => ({ detail: 'Stream failed' }));
        throw new Error(err.detail || 'Stream failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.warmup) {
              firstChunkArrived = true;
              clearTimeout(firstChunkTimer);
              continue;
            }
            if (data.content) {
              firstChunkArrived = true;
              clearTimeout(firstChunkTimer);
              accumulated += data.content;
              setResult(accumulated);
            }
            if (data.done) {
              setStreaming(false);
            }
            if (data.error) {
              toast.error(data.error);
              setStreaming(false);
            }
          } catch { /* skip malformed SSE chunks */ }
        }
      }
      clearTimeout(firstChunkTimer);
      if (!accumulated && firstChunkArrived === false) {
        // Stream closed without any content — fall back
        await runNonStreaming();
      } else if (!accumulated) {
        setResult('No result generated.');
      }
    } catch (e) {
      clearTimeout(firstChunkTimer);
      if (e.name === 'AbortError') {
        // Either user-initiated stop or our timeout-triggered abort.
        // If the timeout fired, runNonStreaming is already in flight via the timer.
        if (firstChunkArrived) toast.info('Generation stopped');
      } else {
        // Stream failed outright — silently fall back to non-streaming.
        await runNonStreaming();
      }
    } finally {
      setLoading(false);
      setStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadAsText = () => {
    const blob = new Blob([result], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${tool.label.replace(/\s/g, '_')}_output.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded as text file');
  };

  const downloadAsHtml = () => {
    const htmlContent = document.getElementById('ai-result-content')?.innerHTML || '';
    const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${tool.label} Output</title><style>body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px;color:#1e293b;line-height:1.7}h1,h2,h3{color:#1e3a5f;margin-top:1.5em}h1{font-size:1.6em;border-bottom:2px solid #3b82f6;padding-bottom:8px}h2{font-size:1.3em}h3{font-size:1.1em}ul,ol{padding-left:1.5em}li{margin-bottom:4px}strong{color:#1e3a5f}table{border-collapse:collapse;width:100%;margin:1em 0}th,td{border:1px solid #e2e8f0;padding:8px 12px;text-align:left}th{background:#f1f5f9;font-weight:600}hr{border:none;border-top:1px solid #e2e8f0;margin:1.5em 0}blockquote{border-left:3px solid #3b82f6;margin:1em 0;padding:8px 16px;background:#f8fafc;color:#475569}</style></head><body>${htmlContent}</body></html>`;
    const blob = new Blob([fullHtml], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${tool.label.replace(/\s/g, '_')}_output.doc`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded as Word document');
  };

  return (
    <div data-testid="recruiter-iq-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>RecruiterIQ AI Suite</h1>
        <p className="text-slate-600 text-sm">AI-powered recruitment tools — 6 specialized tools powered by GPT-5.2</p>
      </div>

      {/* Tool Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" data-testid="tool-selector">
        {TOOLS.map(t => (
          <button key={t.key} data-testid={`tool-${t.key}`}
            onClick={() => {
              // Abort any in-flight stream + reset transient state when switching tools
              try { abortControllerRef.current?.abort(); } catch (err) { console.error("./pages/RecruiterIQ.jsx:", err); }
              abortControllerRef.current = null;
              setLoading(false);
              setStreaming(false);
              setSelectedTool(t.key);
              setResult('');
              setInput('');
              setGuideOpen(true);
            }}
            className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-xs font-semibold ${selectedTool === t.key ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-md' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50/50'}`}>
            <span className={`${t.color} text-white p-1.5 rounded`}><t.icon size={16} /></span>
            <span className="text-center leading-tight">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Help Guide — Collapsible */}
      {guide && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg mb-5 overflow-hidden" data-testid="tool-guide">
          <button onClick={() => setGuideOpen(!guideOpen)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
            <span className="flex items-center gap-2 text-sm font-bold text-blue-900">
              <HelpCircle size={15} className="text-blue-500" />
              How to use {tool?.label} — Purpose, Deliverables & How to Read
            </span>
            {guideOpen ? <ChevronUp size={16} className="text-blue-500" /> : <ChevronDown size={16} className="text-blue-500" />}
          </button>
          {guideOpen && (
            <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs border-t border-blue-200 pt-3">
              <div>
                <p className="font-bold text-blue-900 mb-1 uppercase tracking-wider" style={{ fontSize: '10px' }}>Purpose</p>
                <p className="text-slate-700 leading-relaxed">{guide.purpose}</p>
              </div>
              <div>
                <p className="font-bold text-blue-900 mb-1 uppercase tracking-wider" style={{ fontSize: '10px' }}>What You'll Get</p>
                <p className="text-slate-700 leading-relaxed">{guide.deliverables}</p>
              </div>
              <div>
                <p className="font-bold text-blue-900 mb-1 uppercase tracking-wider" style={{ fontSize: '10px' }}>How to Read the Output</p>
                <p className="text-slate-700 leading-relaxed">{guide.howToRead}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* EEOC & AI Compliance Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg mb-5 overflow-hidden" data-testid="eeoc-compliance-panel">
        <button onClick={() => setComplianceOpen(!complianceOpen)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
          <span className="flex items-center gap-2 text-sm font-bold text-amber-900">
            <Scale size={15} className="text-amber-600" />
            EEOC & AI Compliance Notice
          </span>
          {complianceOpen ? <ChevronUp size={16} className="text-amber-500" /> : <ChevronDown size={16} className="text-amber-500" />}
        </button>
        {complianceOpen && (
          <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-3">
            <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3 border border-amber-100">
              <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-900 mb-1">Equal Employment Opportunity (EEOC) Standards</p>
                <p className="text-xs text-slate-700 leading-relaxed">
                  All AI-generated outputs from RecruiterIQ are intended as decision-support tools only. Federal law prohibits employment discrimination based on race, color, religion, sex (including pregnancy, sexual orientation, and gender identity), national origin, age (40 or older), disability, or genetic information. Users must ensure that AI outputs are not used to unlawfully discriminate against any candidate or applicant.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3 border border-amber-100">
              <Info size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-900 mb-1">AI Output Limitations & Human Review Requirement</p>
                <p className="text-xs text-slate-700 leading-relaxed">
                  AI models may reflect biases present in their training data. Resume screening scores, candidate insights, and role analyses are probabilistic assessments — not definitive evaluations of a candidate's qualifications. <strong>A qualified human must review all AI outputs before making any employment decision.</strong> Do not use AI-generated scores as the sole basis for rejecting, advancing, or ranking candidates.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3 border border-amber-100">
              <Scale size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-900 mb-1">Compliance Responsibility & AI Usage Tracking</p>
                <p className="text-xs text-slate-700 leading-relaxed">
                  Recruitizr logs every AI interaction (tool type and timestamp) via the AI Analytics dashboard. However, full input/output audit trails and candidate-linked decision logs are not yet available. Organizations using AI-assisted screening tools may be subject to adverse impact analysis requirements under Title VII of the Civil Rights Act and local regulations (e.g., NYC Local Law 144). It is the employer's responsibility to maintain their own records of AI-assisted hiring decisions and conduct periodic bias reviews.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      {selectedTool === 'candidate_insights' ? (
        /* ======= CANDIDATE INSIGHTS CUSTOM UI ======= */
        <div className="mb-5 space-y-4" data-testid="candidate-insights-ui">
          {/* Optional candidate name */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <label className="text-xs font-bold text-blue-900 uppercase tracking-wider">Candidate Name (optional)</label>
            <input
              data-testid="ci-candidate-name"
              value={ciCandidateName}
              onChange={e => setCiCandidateName(e.target.value)}
              placeholder="e.g., Aarti Sharma"
              className="mt-2 w-full px-3 py-2 border border-slate-200 rounded text-sm"
            />
          </div>

          {/* Step 1: Upload CV / paste */}
          <div className="bg-white rounded-lg border border-emerald-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                <FileText size={16} className="text-emerald-600" />Step 1: Upload CV / Resume
              </h3>
              <div className="flex items-center gap-1">
                <input type="file" ref={ciResumeUploadRef} accept=".pdf,.docx,.doc,.txt" className="hidden"
                  onChange={e => { if (e.target.files?.[0]) handleCiResumeUpload(e.target.files[0]); }} />
                <Button type="button" size="sm" variant="ghost"
                  className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 text-xs font-bold"
                  data-testid="ci-load-attached-cv" onClick={() => openCvPicker('insights')}>
                  <FileText size={14} className="mr-1.5" />
                  Load from candidate
                </Button>
                <Button type="button" size="sm" variant="ghost" disabled={uploadingCiResume}
                  className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 text-xs font-bold"
                  data-testid="ci-upload-cv-btn" onClick={() => ciResumeUploadRef.current?.click()}>
                  {uploadingCiResume ? <><Loader2 size={14} className="animate-spin mr-1.5" />Extracting...</> : <><Upload size={14} className="mr-1.5" />Upload CV (PDF, DOCX)</>}
                </Button>
              </div>
            </div>
            <Textarea
              data-testid="ci-resume-input"
              value={ciResumeText}
              onChange={e => setCiResumeText(e.target.value)}
              placeholder="Upload a CV/Resume above, or paste candidate information here — name, skills, experience, education, companies worked at..."
              className="rounded border-slate-200 min-h-[150px] text-sm"
            />
            <p className="text-[11px] text-slate-400 mt-2">Either CV/resume or recruiter observations are required — both is best for an objective summary.</p>
          </div>

          {/* Step 2: Recruiter notes */}
          <div className="bg-white rounded-lg border border-amber-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-amber-700">
                <Users size={16} className="text-amber-600" />Step 2: Your Observations (from interaction with candidate)
              </h3>
            </div>
            <Textarea
              data-testid="ci-notes-input"
              value={ciNotes}
              onChange={e => setCiNotes(e.target.value)}
              placeholder="Add your notes from speaking with the candidate — communication skills, attitude, salary expectations discussed, notice period flexibility, cultural fit observations, red flags, strengths you noticed..."
              className="rounded border-slate-200 min-h-[140px] text-sm"
            />
          </div>

          {/* Summary length */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <label className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-3 block">Summary Length</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'brief', label: 'Brief', sub: '~200 words' },
                { value: 'medium', label: 'Medium', sub: '~500 words' },
                { value: 'detailed', label: 'Detailed', sub: '~800+ words' },
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`ci-length-${opt.value}`}
                  onClick={() => setCiLength(opt.value)}
                  className={`p-3 rounded border text-sm transition ${ciLength === opt.value ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                >
                  <div className="font-bold">{opt.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              <strong>Note:</strong> The summary will use only factual, verifiable information from the inputs you provide — no superlatives or evaluative adjectives.
            </p>
          </div>

          {/* Generate Button */}
          <Button data-testid="ci-generate-btn" onClick={handleCandidateInsightsGenerate} disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold w-full py-3">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" />{streaming ? 'Analyzing...' : 'Processing...'}</>
              : <><Sparkles size={16} className="mr-2" />Generate Candidate Insight ({ciLength})</>}
          </Button>
          {streaming && (
            <Button onClick={stopGeneration} variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-lg font-bold">
              <StopCircle size={16} className="mr-2" />Stop
            </Button>
          )}
        </div>
      ) : selectedTool === 'resume_screening' ? (
        /* ======= RESUME SCREENING CUSTOM UI ======= */
        <div className="mb-5 space-y-4" data-testid="resume-screening-ui">
          {/* Mode Tabs */}
          <div className="flex gap-0 border-b border-slate-200">
            <button data-testid="screen-tab-single" onClick={() => setScreenMode('single')}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-b-2 transition-colors ${screenMode === 'single' ? 'border-sky-600 text-sky-700 bg-sky-50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <Search size={15} />Single Resume
            </button>
            <button data-testid="screen-tab-batch" onClick={() => setScreenMode('batch')}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-b-2 transition-colors ${screenMode === 'batch' ? 'border-sky-600 text-sky-700 bg-sky-50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <Users size={15} />Batch Compare & Rank
            </button>
          </div>

          {/* Step 1: Job Description */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-blue-900">
                <FileText size={16} className="text-sky-600" />Step 1: Job Description
              </h3>
              <input type="file" ref={jdUploadRef} accept=".pdf,.docx,.doc,.txt" className="hidden"
                onChange={e => { if (e.target.files?.[0]) handleJDUpload(e.target.files[0]); }} />
              <Button type="button" size="sm" variant="ghost" disabled={uploadingJD}
                className="text-sky-600 hover:text-sky-800 hover:bg-sky-50 text-xs font-bold"
                data-testid="upload-jd-btn" onClick={() => jdUploadRef.current?.click()}>
                {uploadingJD ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
                Upload JD (PDF, DOCX, TXT)
              </Button>
            </div>
            <Textarea data-testid="screen-jd-input" value={jdText} onChange={e => setJdText(e.target.value)}
              placeholder="Paste or upload the Job Description here — include role, skills, experience, responsibilities..."
              className="rounded border-slate-200 min-h-[120px] text-sm" />
          </div>

          {/* Step 2: Resume(s) */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            {screenMode === 'single' ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-blue-900">
                    <Users size={16} className="text-sky-600" />Step 2: Candidate Resume
                  </h3>
                  <div className="flex items-center gap-1">
                    <input type="file" ref={resumeUploadRef} accept=".pdf,.docx,.doc,.txt" className="hidden"
                      onChange={e => { if (e.target.files?.[0]) handleResumeUpload(e.target.files[0]); }} />
                    <Button type="button" size="sm" variant="ghost"
                      className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 text-xs font-bold"
                      data-testid="screen-load-attached-cv" onClick={() => openCvPicker('screen')}>
                      <FileText size={14} className="mr-1.5" />
                      Load from candidate
                    </Button>
                    <Button type="button" size="sm" variant="ghost" disabled={uploadingResume}
                      className="text-sky-600 hover:text-sky-800 hover:bg-sky-50 text-xs font-bold"
                      data-testid="upload-resume-btn" onClick={() => resumeUploadRef.current?.click()}>
                      {uploadingResume ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
                      Upload Resume (PDF, DOCX)
                    </Button>
                  </div>
                </div>
                <Textarea data-testid="screen-resume-input" value={resumeText} onChange={e => setResumeText(e.target.value)}
                  placeholder="Paste or upload the candidate's resume here..."
                  className="rounded border-slate-200 min-h-[120px] text-sm" />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-blue-900">
                    <Users size={16} className="text-sky-600" />Step 2: Upload Candidate Resumes
                  </h3>
                  <input type="file" ref={batchUploadRef} accept=".pdf,.docx,.doc,.txt" multiple className="hidden"
                    onChange={e => { if (e.target.files?.length) handleBatchUpload(Array.from(e.target.files)); }} />
                  <Button type="button" size="sm" variant="ghost" disabled={uploadingBatch}
                    className="text-sky-600 hover:text-sky-800 hover:bg-sky-50 text-xs font-bold"
                    data-testid="upload-batch-btn" onClick={() => batchUploadRef.current?.click()}>
                    {uploadingBatch ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
                    Add Resumes (PDF, DOCX)
                  </Button>
                </div>
                {batchResumes.length > 0 ? (
                  <div className="space-y-2">
                    {batchResumes.map((r, i) => (
                      <div key={`batch-${i}-${r.name}`} className="flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                        <FileText size={16} className="text-sky-600 shrink-0" />
                        <span className="text-sm font-medium text-slate-700 flex-1 truncate">{r.name}</span>
                        <span className="text-[10px] text-slate-400">{r.text.length > 0 ? `${Math.round(r.text.length/100)*100}+ chars` : 'empty'}</span>
                        <button onClick={() => setBatchResumes(prev => prev.filter((_, j) => j !== i))}
                          className="text-red-400 hover:text-red-600 text-xs font-bold px-2">Remove</button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 mt-1">{batchResumes.length} resume(s) ready for comparison</p>
                  </div>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                    <Upload size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">Upload multiple resumes (PDF, DOCX) to compare and rank</p>
                    <Button type="button" size="sm" variant="outline" className="mt-3 text-xs"
                      onClick={() => batchUploadRef.current?.click()}>
                      Select Files
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Generate Button */}
          <Button data-testid="screen-generate-btn" onClick={handleScreenGenerate} disabled={loading}
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-bold w-full py-3">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" />{streaming ? 'Analyzing...' : 'Processing...'}</>
              : <><Sparkles size={16} className="mr-2" />{screenMode === 'single' ? 'Analyze Resume' : `Compare & Rank ${batchResumes.length} Resumes`}</>}
          </Button>
          {streaming && (
            <Button onClick={stopGeneration} variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-lg font-bold">
              <StopCircle size={16} className="mr-2" />Stop
            </Button>
          )}
        </div>
      ) : (
        /* ======= DEFAULT INPUT UI (all other tools) ======= */
        <div className="bg-white rounded-lg border border-slate-200 p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            {tool && <span className={`${tool.color} text-white p-1.5 rounded`}><tool.icon size={16} /></span>}
            <h2 className="font-bold text-blue-900 text-sm">{tool?.label}</h2>
            <span className="text-xs text-slate-500 ml-auto">{tool?.desc}</span>
          </div>
          <Textarea data-testid="iq-input" value={input} onChange={e => setInput(e.target.value)} placeholder={tool?.placeholder}
            className="rounded border-slate-200 min-h-[140px] text-sm font-mono mb-3" />
          {UPLOAD_TOOLS.includes(selectedTool) && (
            <div className="flex items-center gap-3 p-2.5 bg-violet-50 border border-violet-200 rounded-lg mb-3">
              <Upload size={16} className="text-violet-600 flex-shrink-0" />
              <p className="text-xs text-violet-700 flex-1">Upload a <strong>PDF/DOCX</strong> resume to auto-extract text instead of pasting</p>
              <input type="file" ref={cvUploadRef} accept=".pdf,.docx,.doc,.txt" className="hidden"
                onChange={e => { if (e.target.files?.[0]) handleCVUpload(e.target.files[0]); }} />
              <Button type="button" size="sm" variant="outline" disabled={extracting}
                className="border-violet-300 text-violet-700 hover:bg-violet-100 text-xs font-bold"
                data-testid="iq-upload-cv-btn" onClick={() => cvUploadRef.current?.click()}>
                {extracting ? <><Loader2 size={14} className="animate-spin mr-1.5" />Extracting...</> : <><Upload size={14} className="mr-1.5" />Upload CV</>}
              </Button>
            </div>
          )}
          <Button data-testid="iq-generate-btn" onClick={() => generate()} disabled={loading && !streaming}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold w-full">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" />{streaming ? 'Streaming response...' : 'Generating...'}</> : <><Sparkles size={16} className="mr-2" />Generate with AI</>}
          </Button>
          {streaming && (
            <Button data-testid="iq-stop-btn" onClick={stopGeneration} variant="outline"
              className="w-full mt-2 border-red-300 text-red-600 hover:bg-red-50 rounded font-bold">
              <StopCircle size={16} className="mr-2" />Stop Generation
            </Button>
          )}
        </div>
      )}

      {/* Loading State (only show when no result yet) */}
      {loading && !result && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <Loader2 size={36} className="mx-auto animate-spin text-blue-500 mb-3" />
          <p className="text-blue-800 font-semibold">Connecting to AI...</p>
          <p className="text-blue-600 text-sm mt-1">Response will stream in real-time</p>
        </div>
      )}

      {/* Result Area (shows during streaming and after) */}
      {result && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between bg-slate-50 px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              {streaming ? <Loader2 size={16} className="text-blue-600 animate-spin" /> : <Sparkles size={16} className="text-blue-600" />}
              <span className="font-bold text-blue-900 text-sm">
                {streaming ? 'Streaming...' : 'AI Output'} — {tool?.label}
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="copy-result" onClick={copyToClipboard}
                className="text-xs rounded border-slate-300 h-7">
                {copied ? <><Check size={12} className="mr-1" />Copied</> : <><Clipboard size={12} className="mr-1" />Copy</>}
              </Button>
              <Button size="sm" variant="outline" data-testid="download-txt" onClick={downloadAsText}
                className="text-xs rounded border-slate-300 h-7">
                <Download size={12} className="mr-1" />Text
              </Button>
              <Button size="sm" variant="outline" data-testid="download-doc" onClick={downloadAsHtml}
                className="text-xs rounded border-slate-300 h-7">
                <Download size={12} className="mr-1" />Word
              </Button>
            </div>
          </div>
          <div id="ai-result-content" data-testid="iq-result" className="p-6 prose prose-sm prose-slate max-w-none
            prose-headings:text-blue-900 prose-headings:font-bold
            prose-h1:text-xl prose-h1:border-b prose-h1:border-blue-200 prose-h1:pb-2 prose-h1:mb-4
            prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2
            prose-h3:text-base prose-h3:mt-4
            prose-strong:text-blue-900
            prose-li:my-0.5
            prose-table:border-collapse
            prose-th:bg-blue-50 prose-th:text-blue-900 prose-th:border prose-th:border-slate-200 prose-th:px-3 prose-th:py-2
            prose-td:border prose-td:border-slate-200 prose-td:px-3 prose-td:py-2
            prose-hr:border-slate-200
            prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:text-blue-800">
            <ReactMarkdown>{result}</ReactMarkdown>
            {streaming && <span className="inline-block w-2 h-5 bg-blue-500 animate-pulse ml-0.5 align-text-bottom" />}
          </div>
          {/* EEOC AI Disclaimer Footer */}
          {!streaming && (
            <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 flex items-start gap-2" data-testid="ai-disclaimer-footer">
              <ShieldAlert size={14} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 leading-relaxed">
                <strong>AI Disclaimer:</strong> This output is generated by AI and is intended as a decision-support tool only — not a substitute for human judgment. It must not be used as the sole basis for any employment decision. Review all outputs for accuracy and ensure compliance with EEOC non-discrimination standards and applicable local laws before acting on this information.
              </p>
            </div>
          )}
        </div>
      )}

      {/* "Load from attached CV" picker modal — shared by Resume Screening & Candidate Insights */}
      {cvPickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !loadingCvText && setCvPickerOpen(false)}
          data-testid="cv-picker-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-blue-900 text-sm">Load CV from candidate</h3>
                <p className="text-[11px] text-slate-500">Picks the attached CV from a candidate&apos;s resume record.</p>
              </div>
              <button
                type="button"
                onClick={() => setCvPickerOpen(false)}
                disabled={loadingCvText}
                className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-1"
                aria-label="Close"
              >×</button>
            </div>
            <div className="px-5 py-3 border-b border-slate-100">
              <input
                type="text"
                value={cvFilter}
                onChange={e => setCvFilter(e.target.value)}
                placeholder="Search by name, email, phone or position…"
                className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="cv-picker-search"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {cvPickerLoading ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  <Loader2 size={16} className="inline animate-spin mr-2" />Loading…
                </div>
              ) : cvCandidates.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  No candidates with attached CVs yet.<br />
                  <span className="text-xs text-slate-400">
                    Upload a CV from the Resumes page to see candidates here.
                  </span>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {cvCandidates
                    .filter(c => {
                      if (!cvFilter) return true;
                      const f = cvFilter.toLowerCase();
                      return (
                        (c.candidate_name || '').toLowerCase().includes(f) ||
                        (c.email || '').toLowerCase().includes(f) ||
                        (c.phone || '').toLowerCase().includes(f) ||
                        (c.position_title || '').toLowerCase().includes(f)
                      );
                    })
                    .map(c => (
                      <li key={c.id}>
                        <button
                          type="button"
                          disabled={loadingCvText}
                          onClick={() => handleCvPickerSelect(c)}
                          data-testid={`cv-picker-item-${c.id}`}
                          className="w-full text-left px-5 py-3 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-blue-900 truncate">{c.candidate_name || '(unnamed)'}</p>
                              <p className="text-[11px] text-slate-500 truncate">
                                {c.position_title || '—'} {c.email && <>· {c.email}</>} {c.phone && <>· {c.phone}</>}
                              </p>
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                {c.resume_filename}
                                {c.resume_uploaded_at && <> · uploaded {new Date(c.resume_uploaded_at).toLocaleDateString()}</>}
                              </p>
                            </div>
                            <FileText size={14} className="text-emerald-600 mt-1 shrink-0" />
                          </div>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
            {loadingCvText && (
              <div className="px-5 py-2 bg-emerald-50 border-t border-emerald-200 text-xs text-emerald-700">
                <Loader2 size={12} className="inline animate-spin mr-1" />Extracting text from CV…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
